<script setup lang="ts">
import type { ApexOptions } from 'apexcharts'
import VueApexCharts from 'vue3-apexcharts'
import { useLogger } from '#layers/base/composables/useLogger'
import type { Product as CatalogProduct } from '~/types'
import type { Product as SearchProduct } from '~/server/api/search'
import type { CampaignProductItem } from '~/composables/useCampaignProductGroups'
import type { MonthlyItems } from '~/types/order-history'

const props = defineProps<{
  product: CatalogProduct | SearchProduct | CampaignProductItem
  isOpen: boolean
}>()

const log = useLogger('ProductOrderOptimizationSheet')
const { $moment } = useNuxtApp()
const { t } = useI18n()
const { currencyToString } = useHelper()
const orderHistoryInfo = useOrderHistoryInfoStore()

const MONTH_NAMES: Record<number, string> = {
  1: t('months.january.short'),
  2: t('months.february.short'),
  3: t('months.march.short'),
  4: t('months.april.short'),
  5: t('months.may.short'),
  6: t('months.june.short'),
  7: t('months.july.short'),
  8: t('months.august.short'),
  9: t('months.september.short'),
  10: t('months.october.short'),
  11: t('months.november.short'),
  12: t('months.december.short'),
}

const MONTHS_BACK = 12

const chartRef = ref()
const isLoading = ref(false)
const isLoaded = ref(false)
const calculationData = reactive<{
  avgOrderedItems: number
  earningsPerYear: number
  currentOrderItemsCount: number
  countOrdersLastTwelveMonth: number
  monthBasedOrdersMap: Array<{
    monthNumber: number
    monthName: string
    year: number
    value: number
  }>
}>({
  avgOrderedItems: 0,
  earningsPerYear: 0,
  currentOrderItemsCount: 0,
  countOrdersLastTwelveMonth: 0,
  monthBasedOrdersMap: [],
})
const chartOptions = reactive<ApexOptions>({
  chart: {
    toolbar: {
      show: false,
    },
    type: 'bar',
  },
  plotOptions: {
    bar: {
      borderRadius: 5,
      dataLabels: {
        position: 'top',
      },
    },
  },
  dataLabels: {
    formatter(val) {
      // Do not show number if value is below 20 cause the text gets overflowed
      return Number.parseInt(val.toString()) > 0 ? val : ''
    },
    style: {
      fontSize: '12px',
      colors: ['#9b9b9b'],
    },
    offsetY: -20,
  },
  xaxis: {
    labels: {
      style: {
        colors: '#9b9b9b',
      },
    },
    categories: [],
  },
  yaxis: {
    show: false,
  },
})
const chartData = reactive([
  {
    name: t('productOrderOptimization.chart.dataset.label'),
    data: calculationData.monthBasedOrdersMap.map(el => el.value),
  },
])

async function reloadChartData() {
  if (isLoading.value || isLoaded.value)
    return
  isLoading.value = true
  try {
    const data = await orderHistoryInfo.fetchMonthlyOrders(props.product.handle)
    if (data != null && Array.isArray(data))
      processData(data)

    isLoading.value = false
    isLoaded.value = true
  }
  catch (error) {
    log.warn('Could not load product information data for ProductOderOptimization', { error })
  }
}
function processData(monthlyItems: MonthlyItems[]) {
  calculationData.monthBasedOrdersMap = []
  for (let monthIdx = 1; monthIdx <= MONTHS_BACK; monthIdx++) {
    const monthMoment = $moment().subtract(monthIdx, 'months')
    const monthNumber = Number(monthMoment.format('M'))
    const yearNumber = Number(monthMoment.format('YYYY'))
    const monthName = MONTH_NAMES[monthNumber]
    let value = 0

    const foundItems = monthlyItems.filter(item => Number(item.month) === monthNumber && Number(item.year) === yearNumber)
    if (foundItems.length === 1) {
      value = Number.parseInt(foundItems[0].itemCount)
    }
    calculationData.monthBasedOrdersMap.push({
      monthNumber,
      monthName,
      year: yearNumber,
      value,
    })
  }
  // reverse order because we build the table from right to left
  calculationData.monthBasedOrdersMap = calculationData.monthBasedOrdersMap.reverse()
  // count relevant values to show in view
  calculationData.countOrdersLastTwelveMonth = 0
  calculationData.monthBasedOrdersMap.forEach((monthBased) => {
    calculationData.countOrdersLastTwelveMonth
      = calculationData.countOrdersLastTwelveMonth + Number(monthBased.value)
  })

  calculationData.avgOrderedItems = Math.round(
    calculationData.countOrdersLastTwelveMonth / MONTHS_BACK,
  )

  // add also data from current month
  const currentMonthEl = currentMonthObject(monthlyItems)
  calculationData.monthBasedOrdersMap.push(currentMonthEl)
  calculationData.currentOrderItemsCount = currentMonthEl.value

  // calculate earnings for a year

  const foundScalePrices = props.product.scalePrices.filter(
    item => item.minQuantity >= calculationData.avgOrderedItems,
  )
  // possible to get multiple values, we would like to take the first one with the smallest min value
  if (foundScalePrices.length > 0) {
    const scalePrice = foundScalePrices[0]
    const rawPrice = scalePrice.price as number | { amount: number }
    const scaledPrice = typeof rawPrice === 'number' ? rawPrice : rawPrice.amount
    const costsListPrice
        = props.product.listPrice * calculationData.avgOrderedItems * MONTHS_BACK
    const costsScaledPrice
        = scaledPrice * calculationData.avgOrderedItems * MONTHS_BACK
    calculationData.earningsPerYear = costsListPrice - costsScaledPrice
  }

  // Reset chartData
  chartRef.value.updateOptions({
    xaxis: {
      labels: {
        style: {
          colors: '#9b9b9b',
        },
      },
      categories: calculationData.monthBasedOrdersMap.map(el => el.monthName),
    },
  })
  chartRef.value.updateSeries([
    {
      name: t('productOrderOptimization.chart.dataset.label'),
      data: calculationData.monthBasedOrdersMap.map(el => el.value),
    },
  ])
}
function currentMonthObject(monthlyItems: MonthlyItems[]) {
  const currentMoment = $moment()
  const currentMonthNumber = Number(currentMoment.format('M'))
  const currentYearNumber = Number(currentMoment.format('YYYY'))
  const currentMonthName = MONTH_NAMES[currentMonthNumber]
  const currentMonthVal = monthlyItems.find(
    e =>
      Number(e.month) === currentMonthNumber && Number(e.year) === currentYearNumber,
  )
  return {
    monthNumber: currentMonthNumber,
    monthName: currentMonthName,
    year: currentYearNumber,
    value: currentMonthVal ? Number.parseInt(currentMonthVal.itemCount) : 0,
  }
}

watch(() => props.isOpen, (value) => {
  if (isLoading.value)
    return
  if (!value) {
    isLoaded.value = false
    return
  }
  reloadChartData()
}, { immediate: true })
</script>

<template>
  <v-sheet class="d-flex flex-column align-center pa-6 product-order-optimization-sheet">
    <div class="product-order-optimization-bottom-sheet__container">
      <div>
        <div class="mb-6">
          <h2>{{ $t('productOrderOptimization.title') }}</h2>
          <p class="text-heiland-grey">
            {{ product.name }}
          </p>
        </div>
        <div class="mb-6">
          <span class="font-weight-bold">{{ $t('productOrderOptimization.chart.title') }}</span>
          <VueApexCharts
            ref="chartRef"
            data-test-id="product-optimization-chart"
            :options="chartOptions"
            :series="chartData"
          />
        </div>
        <div v-if="calculationData.avgOrderedItems > 0" class="mb-6">
          <p class="font-weight-bold">
            {{ $t('productOrderOptimization.avgOrderedItems') }}
          </p>
          <p class="font-weight-bold text-heiland-grey">
            Ø {{ calculationData.avgOrderedItems }}
          </p>
        </div>
        <div>
          <p class="font-weight-bold">
            {{ $t('productOrderOptimization.optimization.title') }}
          </p>
          <i18n-t
            v-if="calculationData.earningsPerYear > 0"
            keypath="productOrderOptimization.optimization.text"
            class="text-heiland-grey"
            tag="p"
          >
            <template #earningsPerYear>
              <span class="font-weight-bold text-success">
                {{ currencyToString(calculationData.earningsPerYear) }}
              </span>
            </template>
          </i18n-t>
          <p v-if="calculationData.earningsPerYear <= 0" class="text-heiland-grey">
            {{ $t('productOrderOptimization.optimization.lessDataText') }}
          </p>
        </div>
      </div>
    </div>
  </v-sheet>
</template>

<style lang="scss">
.product-order-optimization-bottom-sheet {
  &__container {
    width: 450px;
    max-width: 100%;
  }
}
</style>
